import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { BreakdownDimension } from '@apis/productivity';
import type { IProductivityCustomRange, IProductivityTimeframe } from '@interfaces/IProductivity';
import { customRangeFor, fmtLocal, parseLocalDay, resolveProductivityRange } from '../utils/productivityRange';

// The page's URL vocabulary: Month and Year as their dates, `?from=…&to=…`;
// `?timeframe=custom&from=…&to=…`; and `?tab=location`. A default value is left
// out, so the plain page URL stays plain.
const TIMEFRAME_PARAM = 'timeframe';
const FROM_PARAM = 'from';
const TO_PARAM = 'to';
const TAB_PARAM = 'tab';

const TIMEFRAMES: readonly IProductivityTimeframe[] = ['thisWeek', 'thisMonth', 'thisYear', 'custom'];
const TABS: readonly BreakdownDimension[] = ['activities', 'location', 'subcontractors', 'workers'];
const DEFAULT_TIMEFRAME: IProductivityTimeframe = 'thisWeek';
const DEFAULT_TAB: BreakdownDimension = 'activities';

/** Written to the URL as their dates, not their name. */
const DATED_TIMEFRAMES: readonly IProductivityTimeframe[] = ['thisMonth', 'thisYear'];
const NO_CUSTOM_RANGE: IProductivityCustomRange = { from: null, to: null };

/** What the page shows: the timeframe (with the custom dates) and the open tab. */
export interface IProductivityView {
  timeframe: IProductivityTimeframe;
  customRange: IProductivityCustomRange;
  tab: BreakdownDimension;
}

/** A dated timeframe's window today, as the picker's dates. */
function datedRange(timeframe: IProductivityTimeframe, tz: string): IProductivityCustomRange {
  const range = resolveProductivityRange(timeframe, NO_CUSTOM_RANGE, tz);
  return range ? customRangeFor(range) : NO_CUSTOM_RANGE;
}

/** Dates without a timeframe name: Month or Year when they are that window
 *  today, otherwise a custom range — a link opened later keeps its dates. */
function timeframeOfDates(customRange: IProductivityCustomRange, tz: string): IProductivityTimeframe {
  const { from, to } = customRange;
  if (!from || !to) return DEFAULT_TIMEFRAME;
  const matches = (timeframe: IProductivityTimeframe) => {
    const window = datedRange(timeframe, tz);
    return window.from?.getTime() === from.getTime() && window.to?.getTime() === to.getTime();
  };
  return DATED_TIMEFRAMES.find(matches) ?? 'custom';
}

/** The view a URL describes; anything malformed falls back to the default. */
function readView(params: URLSearchParams, tz: string): IProductivityView {
  const customRange = { from: parseLocalDay(params.get(FROM_PARAM)), to: parseLocalDay(params.get(TO_PARAM)) };
  return {
    timeframe:
      TIMEFRAMES.find((value) => value === params.get(TIMEFRAME_PARAM)) ?? timeframeOfDates(customRange, tz),
    customRange,
    tab: TABS.find((value) => value === params.get(TAB_PARAM)) ?? DEFAULT_TAB,
  };
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null): void {
  if (value === null) params.delete(key);
  else params.set(key, value);
}

/** `prev` with the view's params rewritten from `view`. Params that are not the
 *  view's survive. Dates ride with a custom timeframe (its picks) and with Month
 *  and Year (today's window); only custom also names itself. */
function withViewParams(prev: URLSearchParams, view: IProductivityView, tz: string): URLSearchParams {
  const next = new URLSearchParams(prev);
  const custom = view.timeframe === 'custom';
  const dates = custom
    ? view.customRange
    : DATED_TIMEFRAMES.includes(view.timeframe)
      ? datedRange(view.timeframe, tz)
      : NO_CUSTOM_RANGE;
  setOrDelete(next, TIMEFRAME_PARAM, custom ? 'custom' : null);
  setOrDelete(next, FROM_PARAM, dates.from ? fmtLocal(dates.from) : null);
  setOrDelete(next, TO_PARAM, dates.to ? fmtLocal(dates.to) : null);
  setOrDelete(next, TAB_PARAM, view.tab === DEFAULT_TAB ? null : view.tab);
  return next;
}

/**
 * The page's view, kept in the URL rather than in component state: a refresh
 * lands on the same timeframe and tab, and the address bar is a link to what
 * is on screen. Writes replace rather than push, so flicking through
 * timeframes does not bury the previous page under back-button steps.
 */
export function useProductivityView(
  tz: string,
): IProductivityView & { setView: (patch: Partial<IProductivityView>) => void } {
  const [params, setParams] = useSearchParams();
  const { timeframe, tab } = readView(params, tz);
  const from = params.get(FROM_PARAM);
  const to = params.get(TO_PARAM);
  // Stable across renders for the same URL — the range memo keys on it.
  const customRange = useMemo(() => ({ from: parseLocalDay(from), to: parseLocalDay(to) }), [from, to]);

  // Call this once per change. If two fields change together (Custom sets the
  // timeframe AND the dates), pass both in one patch: react-router's updater
  // starts from the params of the last render, not the current URL, so a
  // second call in the same handler would overwrite the first.
  const setView = useCallback(
    (patch: Partial<IProductivityView>) => {
      setParams((prev) => withViewParams(prev, { ...readView(prev, tz), ...patch }, tz), { replace: true });
    },
    [setParams, tz],
  );

  return { timeframe, customRange, tab, setView };
}
